import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Search, Landmark, CheckCircle2, Pencil } from "lucide-react";

const EMPTY = [];

function EditBankDialog({ bank, categories, open, onOpenChange }) {
  const qc = useQueryClient();
  const [lender, setLender] = useState({ min_income: 0, max_ltv: 0, active: false });
  const [rows, setRows] = useState({});

  const { data: policies = EMPTY } = useQuery({
    queryKey: ["policies", bank?.code],
    queryFn: async () => (await api.get("/policies", { params: { bank_code: bank.code } })).data,
    enabled: !!bank?.code && open,
  });

  useEffect(() => {
    if (!bank) return;
    setLender({ min_income: bank.min_income ?? 0, max_ltv: bank.max_ltv ?? 0, active: !!bank.active });
  }, [bank]);
  useEffect(() => {
    const m = {};
    categories.forEach((c) => {
      const p = policies.find((x) => x.category_code === c.code);
      m[c.code] = { foir: p?.foir ?? "", multiplier: p?.multiplier ?? "", interest_rate: p?.interest_rate ?? "" };
    });
    setRows(m);
  }, [policies, categories]);

  const save = useMutation({
    mutationFn: async () => {
      await api.patch(`/lenders/${bank.code}`, { min_income: Number(lender.min_income), max_ltv: Number(lender.max_ltv), active: lender.active });
      const puts = categories
        .filter((c) => rows[c.code]?.foir !== "" && rows[c.code]?.multiplier !== "" && rows[c.code]?.interest_rate !== "")
        .map((c) => api.put("/policies", { bank_code: bank.code, category_code: c.code, foir: Number(rows[c.code].foir), multiplier: Number(rows[c.code].multiplier), interest_rate: Number(rows[c.code].interest_rate) }));
      await Promise.all(puts);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bank-notes-lenders"] });
      qc.invalidateQueries({ queryKey: ["bank-notes-policies"] });
      qc.invalidateQueries({ queryKey: ["policies", bank.code] });
      qc.invalidateQueries({ queryKey: ["lenders"] });
      toast.success("Bank note saved — synced with the mobile app");
      onOpenChange(false);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const setRow = (cat, field, val) => setRows((r) => ({ ...r, [cat]: { ...r[cat], [field]: val } }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white dark:bg-slate-900" data-testid="bank-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit · {bank?.name}</DialogTitle>
          <DialogDescription>Update eligibility and per-category lending rules. Changes sync instantly with the mobile app.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-3 gap-4 items-end">
            <div className="space-y-1.5"><Label>Min Income (₹)</Label><Input type="number" value={lender.min_income} onChange={(e) => setLender({ ...lender, min_income: e.target.value })} data-testid="bank-edit-min-income" /></div>
            <div className="space-y-1.5"><Label>Max LTV (0-1)</Label><Input type="number" step="0.01" value={lender.max_ltv} onChange={(e) => setLender({ ...lender, max_ltv: e.target.value })} data-testid="bank-edit-max-ltv" /></div>
            <label className="flex items-center gap-2 h-10"><Checkbox checked={lender.active} onCheckedChange={(v) => setLender({ ...lender, active: !!v })} data-testid="bank-edit-active" /><span className="text-sm">Active</span></label>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Policy by Category</div>
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead className="text-xs uppercase">Category</TableHead><TableHead className="text-xs uppercase">FOIR</TableHead><TableHead className="text-xs uppercase">Multiplier</TableHead><TableHead className="text-xs uppercase">ROI %</TableHead></TableRow></TableHeader>
              <TableBody>
                {categories.map((c) => (
                  <TableRow key={c.code} className="hover:bg-transparent">
                    <TableCell className="font-medium" title={c.description}>{c.code}</TableCell>
                    <TableCell><Input type="number" step="0.01" className="h-8 w-24" value={rows[c.code]?.foir ?? ""} onChange={(e) => setRow(c.code, "foir", e.target.value)} data-testid={`bank-edit-foir-${c.code}`} /></TableCell>
                    <TableCell><Input type="number" step="0.1" className="h-8 w-24" value={rows[c.code]?.multiplier ?? ""} onChange={(e) => setRow(c.code, "multiplier", e.target.value)} data-testid={`bank-edit-mult-${c.code}`} /></TableCell>
                    <TableCell><Input type="number" step="0.01" className="h-8 w-24" value={rows[c.code]?.interest_rate ?? ""} onChange={(e) => setRow(c.code, "interest_rate", e.target.value)} data-testid={`bank-edit-roi-${c.code}`} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-blue-700 hover:bg-blue-800 text-white" disabled={save.isPending} onClick={() => save.mutate()} data-testid="bank-edit-save">{save.isPending ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function BankNotes() {
  const { user } = useAuth();
  const canEdit = ["Admin", "Manager"].includes(user?.role);
  const [search, setSearch] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);
  const [editing, setEditing] = useState(null);

  const { data: lenders = [], isLoading } = useQuery({ queryKey: ["bank-notes-lenders"], queryFn: async () => (await api.get("/lenders")).data });
  const { data: policies = [] } = useQuery({ queryKey: ["bank-notes-policies"], queryFn: async () => (await api.get("/policies")).data });
  const { data: categories = EMPTY } = useQuery({ queryKey: ["policy-categories"], queryFn: async () => (await api.get("/policies/categories")).data });

  const policiesByBank = useMemo(() => {
    const m = {};
    policies.forEach((p) => { (m[p.bank_code] ||= []).push(p); });
    return m;
  }, [policies]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return lenders
      .filter((l) => (onlyActive ? l.active : true))
      .filter((l) => !q || [l.name, l.code, l.type].some((v) => String(v || "").toLowerCase().includes(q)));
  }, [lenders, search, onlyActive]);

  return (
    <div data-testid="bank-notes-page">
      <PageHeader title="Bank Notes" subtitle="Lender eligibility and per-category lending rules. Edits sync instantly with the mobile app." />

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative max-w-xs w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="bank-notes-search-input" placeholder="Search bank, type…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white dark:bg-slate-950" />
        </div>
        <button onClick={() => setOnlyActive((v) => !v)} data-testid="bank-notes-active-toggle"
          className={`text-sm px-3 py-2 rounded-lg border transition-colors ${onlyActive ? "bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-900 dark:text-emerald-300" : "bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-500"}`}>
          {onlyActive ? "Active banks only" : "All banks"}
        </button>
        <span className="text-sm text-slate-500">{filtered.length} banks</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="bank-notes-grid">
        {isLoading && Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-48 w-full rounded-xl" />)}
        {!isLoading && filtered.length === 0 && <div className="col-span-full text-center text-slate-400 py-10">No banks found.</div>}
        {!isLoading && filtered.map((l) => {
          const bankPolicies = (policiesByBank[l.code] || []).sort((a, b) => a.category_code.localeCompare(b.category_code));
          return (
            <Card key={l.id || l.code} data-testid={`bank-note-${l.code}`} className="p-5 border-slate-200 dark:border-slate-800">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-9 w-9 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center"><Landmark className="h-4 w-4 text-blue-600" /></div>
                  <div><div className="font-semibold text-slate-900 dark:text-slate-100 leading-tight">{l.name}</div><div className="text-xs text-slate-400">{l.code}</div></div>
                </div>
                <div className="flex items-center gap-1.5">
                  {l.active && <Badge className="border-0 bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300"><CheckCircle2 className="h-3 w-3 mr-1" />Active</Badge>}
                  {canEdit && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(l)} data-testid={`bank-edit-${l.code}`}><Pencil className="h-4 w-4 text-slate-500" /></Button>}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Type</div><div className="text-xs font-medium text-slate-700 dark:text-slate-200 mt-0.5">{l.type || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Min Income</div><div className="text-xs font-num font-semibold text-slate-700 dark:text-slate-200 mt-0.5">₹{(l.min_income || 0).toLocaleString("en-IN")}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Max LTV</div><div className="text-xs font-num font-semibold text-slate-700 dark:text-slate-200 mt-0.5">{l.max_ltv ? `${Math.round(l.max_ltv * 100)}%` : "—"}</div></div>
              </div>

              {bankPolicies.length > 0 && (
                <div className="mt-4 border-t border-slate-100 dark:border-slate-800 pt-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Policy (FOIR · Mult · ROI)</div>
                  <div className="space-y-1">
                    {bankPolicies.map((p) => (
                      <div key={p.id} className="flex items-center justify-between text-xs">
                        <span className="text-slate-500 font-medium">{p.category_code}</span>
                        <span className="font-num text-slate-700 dark:text-slate-300">{Math.round(p.foir * 100)}% · {p.multiplier}x · <span className="font-semibold text-blue-700 dark:text-blue-400">{p.interest_rate}%</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {editing && <EditBankDialog bank={editing} categories={categories} open={!!editing} onOpenChange={(o) => !o && setEditing(null)} />}
    </div>
  );
}
